import React, { useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    useTheme,
    useMediaQuery,
    Button,
    Stack,
    Grid,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GroupsIcon from "@mui/icons-material/Groups";
import SchoolIcon from "@mui/icons-material/School";
import UpdateIcon from "@mui/icons-material/Update";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PeopleIcon from "@mui/icons-material/People";
import { motion } from "framer-motion";

// =====================================================
// CONFIGURATION
// =====================================================
const FACEBOOK_PAGE_URL = "https://www.facebook.com/thefigapp";
const FOLLOWER_COUNT = "1,146";
const COMPANY_NAME = "Swan Solutions";
const PAGE_TITLE = "Social Feed | Swan Solutions - Connect With Us";
const PAGE_DESCRIPTION = "Stay connected with Swan Solutions. Follow our Facebook page for the latest updates.";
const PAGE_KEYWORDS = "Swan Solutions, K-12, Facebook, FIG";

// =====================================================
// SEO COMPONENT
// =====================================================
const SEOHead = ({ title, description, keywords }) => {
    useEffect(() => {
        document.title = title;
        const setMeta = (name, content, isProperty = false) => {
            const attr = isProperty ? "property" : "name";
            let meta = document.querySelector(`meta[${attr}="${name}"]`);
            if (!meta) {
                meta = document.createElement("meta");
                meta.setAttribute(attr, name);
                document.head.appendChild(meta);
            }
            meta.setAttribute("content", content);
        };
        setMeta("description", description);
        setMeta("keywords", keywords);
        setMeta("og:title", title, true);
        setMeta("og:description", description, true);
        return () => { document.title = COMPANY_NAME; };
    }, [title, description, keywords]);
    return null;
};

// =====================================================
// STYLES
// =====================================================
const glassStyles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(255, 255, 255, 0.7)",
        boxShadow: "0 20px 60px rgba(0, 51, 102, 0.12)",
    },
};

const triggerHaptic = (style = "light") => {
    if (navigator.vibrate) {
        navigator.vibrate(style === "medium" ? [20] : [10]);
    }
};

// =====================================================
// HAPTIC BUTTON
// =====================================================
const HapticButton = ({ children, onClick, hapticStyle = "medium", ...props }) => (
    <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
        <Button onClick={(e) => { triggerHaptic(hapticStyle); onClick?.(e); }} {...props}>
            {children}
        </Button>
    </motion.div>
);

// =====================================================
// FEATURE ITEM
// =====================================================
const FeatureItem = ({ icon, title, description }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #1877F2 0%, #0055A4 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 6px 20px rgba(24,119,242,0.3)",
                }}
            >
                {React.cloneElement(icon, { sx: { color: "white", fontSize: 22 } })}
            </Box>
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                    {description}
                </Typography>
            </Box>
        </Box>
    );
};

// =====================================================
// MAIN COMPONENT
// =====================================================
export default function Social() {
    const theme = useTheme();

    return (
        <>
            <SEOHead title={PAGE_TITLE} description={PAGE_DESCRIPTION} keywords={PAGE_KEYWORDS} />

            <Box
                component="section"
                sx={{
                    minHeight: "100vh",
                    background: `
                        radial-gradient(ellipse at 10% 20%, rgba(0, 207, 254, 0.12) 0%, transparent 50%),
                        radial-gradient(ellipse at 90% 80%, rgba(0, 51, 102, 0.08) 0%, transparent 50%),
                        linear-gradient(180deg, ${theme.palette.background.default} 0%, #e3f2fd 100%)
                    `,
                    py: { xs: 2, md: 3 },
                }}
            >
                <Container maxWidth="xl">
                    <Grid container spacing={3}>
                        {/* LEFT SIDE - Content */}
                        <Grid item xs={12} lg={3}>
                            <Box
                                component={motion.div}
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                sx={{
                                    position: { lg: "sticky" },
                                    top: { lg: 80 },
                                }}
                            >
                                {/* Hero Card */}
                                <Box
                                    sx={{
                                        ...glassStyles.glassCard,
                                        borderRadius: "24px",
                                        p: 3,
                                        mb: 3,
                                        textAlign: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            borderRadius: "20px",
                                            background: "linear-gradient(135deg, #1877F2 0%, #0055A4 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mx: "auto",
                                            mb: 2,
                                            boxShadow: "0 12px 40px rgba(24,119,242,0.4)",
                                        }}
                                    >
                                        <FacebookIcon sx={{ fontSize: 38, color: "white" }} />
                                    </Box>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: { xs: "1.6rem", md: "1.8rem" },
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #1877F2 100%)`,
                                            backgroundClip: "text",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            mb: 1.5,
                                        }}
                                    >
                                        Connect With Us
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Join our growing community of educators and school nutrition professionals.
                                    </Typography>

                                    {/* Follower Count */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            mb: 3,
                                            py: 1.5,
                                            px: 3,
                                            borderRadius: "14px",
                                            background: "rgba(24,119,242,0.08)",
                                            mx: "auto",
                                            width: "fit-content",
                                        }}
                                    >
                                        <PeopleIcon sx={{ color: "#1877F2", fontSize: 24 }} />
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#1877F2" }}>
                                            {FOLLOWER_COUNT}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Followers
                                        </Typography>
                                    </Box>

                                    <Stack spacing={1.5}>
                                        <HapticButton
                                            variant="contained"
                                            fullWidth
                                            startIcon={<FacebookIcon />}
                                            href={FACEBOOK_PAGE_URL}
                                            target="_blank"
                                            sx={{
                                                background: "linear-gradient(135deg, #1877F2 0%, #0a5dc2 100%)",
                                                py: 1.3,
                                                borderRadius: "12px",
                                                boxShadow: "0 8px 25px rgba(24,119,242,0.35)",
                                            }}
                                        >
                                            Follow Page
                                        </HapticButton>
                                        <HapticButton
                                            variant="outlined"
                                            fullWidth
                                            endIcon={<OpenInNewIcon />}
                                            href={FACEBOOK_PAGE_URL}
                                            target="_blank"
                                            sx={{
                                                borderColor: "#1877F2",
                                                color: "#1877F2",
                                                py: 1.2,
                                                borderRadius: "12px",
                                                borderWidth: 2,
                                            }}
                                        >
                                            Open in Facebook
                                        </HapticButton>
                                    </Stack>
                                </Box>

                                {/* Features Card */}
                                <Box
                                    sx={{
                                        ...glassStyles.glassCard,
                                        borderRadius: "24px",
                                        p: 3,
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                                        Why Follow Us?
                                    </Typography>
                                    <FeatureItem icon={<UpdateIcon />} title="Latest Updates" description="Product news and announcements" />
                                    <FeatureItem icon={<SchoolIcon />} title="K-12 Insights" description="Tips for education technology" />
                                    <FeatureItem icon={<GroupsIcon />} title="Community" description="Connect with other districts" />
                                    <FeatureItem icon={<EmojiEventsIcon />} title="Events" description="Webinars and training sessions" />
                                </Box>
                            </Box>
                        </Grid>

                        {/* RIGHT SIDE - Facebook Feed - FULL WIDTH */}
                        <Grid item xs={12} lg={9}>
                            <Box
                                component={motion.div}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                sx={{
                                    ...glassStyles.glassCard,
                                    borderRadius: "28px",
                                    overflow: "hidden",
                                    height: { xs: 900, md: 1100, lg: 1300 },
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {/* Header */}
                                <Box
                                    sx={{
                                        background: "linear-gradient(135deg, #1877F2 0%, #0055A4 100%)",
                                        px: 3,
                                        py: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "12px",
                                            backgroundColor: "rgba(255,255,255,0.2)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <FacebookIcon sx={{ color: "white", fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" sx={{ color: "white", fontWeight: 700 }}>
                                            FIG K12
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                                            Latest Posts • {FOLLOWER_COUNT} followers
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Facebook Feed Container - SCALED TO FILL */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        width: "100%",
                                        background: "linear-gradient(180deg, #f0f2f5 0%, #e4e6eb 100%)",
                                        overflow: "hidden",
                                        position: "relative",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: "flex",
                                            justifyContent: "center",
                                            "& iframe": {
                                                transform: {
                                                    xs: "scale(1.0)",
                                                    sm: "scale(1.3)",
                                                    md: "scale(1.6)",
                                                    lg: "scale(1.9)",
                                                    xl: "scale(2.1)",
                                                },
                                                transformOrigin: "top center",
                                            },
                                        }}
                                    >
                                        <iframe
                                            src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(FACEBOOK_PAGE_URL)}&tabs=timeline&width=500&height=800&small_header=false&adapt_container_width=false&hide_cover=false&show_facepile=true`}
                                            width="500"
                                            height="800"
                                            style={{
                                                border: "none",
                                                overflow: "hidden",
                                            }}
                                            scrolling="no"
                                            frameBorder="0"
                                            allowFullScreen={true}
                                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                            title="FIG K12 Facebook Page"
                                        />
                                    </Box>
                                </Box>

                                {/* Footer */}
                                <Box
                                    sx={{
                                        p: 2,
                                        borderTop: "1px solid rgba(0,51,102,0.1)",
                                        textAlign: "center",
                                        backgroundColor: "white",
                                        flexShrink: 0,
                                    }}
                                >
                                    <HapticButton
                                        variant="contained"
                                        startIcon={<FacebookIcon />}
                                        endIcon={<OpenInNewIcon />}
                                        href={FACEBOOK_PAGE_URL}
                                        target="_blank"
                                        fullWidth
                                        sx={{
                                            background: "linear-gradient(135deg, #1877F2 0%, #0a5dc2 100%)",
                                            borderRadius: "14px",
                                            py: 1.5,
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            maxWidth: 500,
                                            mx: "auto",
                                        }}
                                    >
                                        View All on Facebook
                                    </HapticButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
}